import { ArrowRight, Award, ExternalLink, LoaderCircle } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { apiErrorMessage } from '../api/client'
import { partnersApi } from '../api/endpoints'
import type { DegreeStatus, VioletDotsStatus } from '../types/api'
import { openHoldingTab } from '../utils/holdingTab'
import { ErrorMessage } from './ErrorMessage'

const DEGREE_OPTIONS: { value: DegreeStatus; label: string }[] = [
  { value: 'undergraduate', label: 'I am studying for a degree' },
  { value: 'graduate', label: 'I have a degree' },
  { value: 'none', label: 'Neither' },
]

/** Why the button is not offered, in the person's own terms. */
const NOT_FOR_YOU: Partial<Record<VioletDotsStatus['eligibility'], string>> = {
  UNDER_18: 'AI Certification on Violet Dots is for people aged 18 and over.',
  NO_DEGREE: 'AI Certification on Violet Dots is for students studying for a degree, and for graduates.',
  INSTITUTION: 'AI Certification is taken by people, one at a time - this account cannot take it.',
  EMAIL_NOT_VERIFIED: 'Sign in once with a join code to confirm your email, then come back here.',
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : null

/**
 * The AI Certification card's body: certificates from Violet Dots, and "Get certified on Violet
 * Dots", which our server turns into a signed link that signs them in there (VioletDotsService).
 *
 * Violet Dots takes only people 18 or over who are studying for a degree or hold one, so the first
 * press asks whatever we do not know yet - the degree question once (it is remembered), and "I am
 * 18 or over" when we hold no date of birth. Read again whenever the tab comes back into view: they
 * sit the test in a new tab, so returning here should show the new certificate.
 */
export function VioletDotsCertification() {
  const [status, setStatus] = useState<VioletDotsStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [asking, setAsking] = useState(false)
  const [degree, setDegree] = useState<DegreeStatus | null>(null)
  const [adult, setAdult] = useState(false)
  const [opening, setOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set when the last press could not sign them in (the link is not set up on our side yet), so the
  // page says they will have to sign in on Violet Dots rather than leaving them to wonder why.
  const [notSignedIn, setNotSignedIn] = useState(false)

  const load = useCallback(() => {
    partnersApi
      .violetDots()
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
    const onVisible = () => document.visibilityState === 'visible' && load()
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [load])

  const eligibility = status?.eligibility
  const needsDegree = eligibility === 'ASK_DEGREE' || (eligibility === 'ASK_AGE' && !status?.degreeStatus)
  const needsAge = eligibility === 'ASK_AGE'
  const certificates = status?.certificates.status === 'LINKED' ? status.certificates.certificates : []

  async function go() {
    setError(null)
    if ((needsDegree || needsAge) && !asking) {
      setAsking(true)
      return
    }
    if (needsDegree && !degree) {
      setError('Choose one option to continue.')
      return
    }
    if (needsAge && !adult) {
      setError('Confirm that you are 18 or over to continue.')
      return
    }
    // Opened during the click, or a popup blocker stops it - see openHoldingTab.
    const tab = degree === 'none' ? null : openHoldingTab('AI Certification')
    setOpening(true)
    try {
      setNotSignedIn(false)
      const { url, signedIn } = await partnersApi.violetDotsHandoff({
        degreeStatus: needsDegree ? degree ?? undefined : undefined,
        confirmsAdult: needsAge ? adult : undefined,
      })
      if (tab) tab.location.href = url
      else window.location.href = url
      setNotSignedIn(!signedIn)
      setAsking(false)
    } catch (err) {
      tab?.close()
      setError(apiErrorMessage(err, 'Could not open Violet Dots. Please try again.'))
    } finally {
      setOpening(false)
      load()
    }
  }

  const box = 'mt-4 w-full rounded-xl border border-ink-200/70 bg-ink-50 p-3 text-sm'
  const heading = <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-400">Your certificates</div>

  if (loading && !status) {
    return (
      <div className={`${box} flex items-center gap-2 text-ink-500`}>
        <LoaderCircle size={14} className="animate-spin" /> Checking your certificates...
      </div>
    )
  }

  const refusal = eligibility ? NOT_FOR_YOU[eligibility] : undefined

  return (
    <>
      <div className={box}>
        {heading}
        {certificates.length > 0 ? (
          <ul className="mt-2 space-y-2">
            {certificates.map((c) => (
              <li key={c.code} className="flex items-start gap-2.5 rounded-lg bg-white p-2.5">
                <Award size={18} className="mt-0.5 shrink-0 text-violet-600" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ink-900">{c.levelName ?? c.level}</div>
                  <div className="text-xs text-ink-500">
                    {[c.percentileBand, fmtDate(c.certifiedOn), c.code].filter(Boolean).join(' · ')}
                  </div>
                </div>
                {c.url && (
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View certificate ${c.code} (opens in a new tab)`}
                    className="flex shrink-0 items-center gap-1 text-xs font-semibold text-ink-900 hover:underline"
                  >
                    View <ExternalLink size={12} aria-hidden="true" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-ink-500">
            {status?.certificates.status === 'LINKED'
              ? 'No certificate yet - pass the assessment on Violet Dots and it will show here.'
              : 'Get certified on Violet Dots and your certificates will show here.'}
          </p>
        )}
      </div>

      {refusal ? (
        <p className="mt-4 rounded-lg bg-ink-100 px-3 py-2 text-sm text-ink-600">{refusal}</p>
      ) : (
        <>
          {asking && (
            <fieldset className="mt-4 w-full space-y-2">
              {needsDegree && (
                <>
                  <legend className="mb-1 text-sm font-medium text-ink-700">
                    Violet Dots is for students and graduates. Which describes you?
                  </legend>
                  {DEGREE_OPTIONS.map((o) => (
                    <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
                      <input
                        type="radio"
                        name="degree"
                        value={o.value}
                        checked={degree === o.value}
                        onChange={() => setDegree(o.value)}
                      />
                      {o.label}
                    </label>
                  ))}
                </>
              )}
              {needsAge && (
                <label className="flex cursor-pointer items-center gap-2 pt-1 text-sm text-ink-700">
                  <input type="checkbox" checked={adult} onChange={(e) => setAdult(e.target.checked)} />I am 18 or
                  over
                </label>
              )}
            </fieldset>
          )}
          <div className="mt-3 w-full">
            <ErrorMessage message={error} />
          </div>
          <button
            type="button"
            onClick={() => void go()}
            disabled={opening}
            aria-label="Get certified on Violet Dots (opens in a new tab)"
            className="btn-sun mt-1 gap-1.5 disabled:cursor-wait"
          >
            {opening ? (
              <>
                <LoaderCircle size={15} className="animate-spin" /> Opening...
              </>
            ) : (
              <>
                {certificates.length > 0 ? 'Open Violet Dots' : asking ? 'Continue' : 'Get certified on Violet Dots'}{' '}
                <ArrowRight size={15} />
              </>
            )}
          </button>
          {notSignedIn && (
            <p role="status" className="mt-3 text-sm text-ink-500">
              We could not sign you in automatically this time, so Violet Dots opened at its own home page. Sign in
              there with this email address.
            </p>
          )}
        </>
      )}
    </>
  )
}
